import React from 'react';
import './KPICard.css';

interface KPICardProps {
  title: string;
  value: string;
  formatValue?: boolean; // New prop to enable/disable formatting
  color?: 'blue' | 'green' | 'purple' | 'orange';
  icon?: React.ReactNode;
  onRefresh?: () => Promise<string>;
  loading?: boolean;
  buttonText?: string;
  onButtonClick?: () => void;
  buttonDisabled?: boolean;
  disabled?: boolean;
  disabledMessage?: string;
}

// Helper function to format values
const formatNumberValue = (value: string): string => {
  // Parse the input value to a number
  const num = parseFloat(value);
  
  // Check if it's a valid number
  if (isNaN(num)) return value;
  
  // Format with K, M, B suffixes
  if (num >= 1000000000) {
    return (num / 1000000000).toFixed(2) + 'B';
  } else if (num >= 1000000) {
    return (num / 1000000).toFixed(2) + 'M';
  } else if (num >= 1000) {
    return (num / 1000).toFixed(2) + 'K';
  } else if (num % 1 !== 0) {
    // If it's a decimal, limit to 2 decimal places
    return num.toFixed(2);
  } else {
    // For small integers, just return as is
    return num.toString();
  }
};

const KPICard: React.FC<KPICardProps> = ({ 
  title, 
  value, 
  formatValue = false, // Default to false for backward compatibility
  color = 'blue', 
  icon,
  onRefresh,
  loading = false,
  buttonText,
  onButtonClick,
  buttonDisabled = false,
  disabled = false,
  disabledMessage = "Not available"
}) => {
  const handleRefresh = async () => {
    if (onRefresh && !loading) {
      onRefresh();
    }
  };
  
  // Format the display value if formatValue is true
  const displayValue = formatValue ? formatNumberValue(value) : value;
  
  return (
    <div className={`kpi-card ${color} ${disabled ? 'disabled' : ''}`}>
      <div className="kpi-card-header">
        <h3 className="kpi-card-title">{title}</h3>
        {onRefresh && (
          <button 
            className="refresh-button" 
            onClick={handleRefresh}
            disabled={loading || disabled}
          >
            {loading ? (
              <span className="loader"></span>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.3"/>
              </svg>
            )}
          </button>
        )}
      </div>
      <div className="kpi-card-content">
        {disabled ? (
          <div className="kpi-disabled-message">{disabledMessage}</div>
        ) : (
          <>
            <span className="kpi-card-value">
              {icon && <span className="kpi-card-icon">{icon}</span>}
              {displayValue}
            </span>
          </>
        )}
      </div>
      {buttonText && onButtonClick && (
        <div className="kpi-card-footer">
          <button 
            className="kpi-action-button" 
            onClick={onButtonClick}
            disabled={buttonDisabled || disabled}
          >
            {buttonText}
          </button>
        </div>
      )}
    </div>
  );
};

export default KPICard;